import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Box, Chip, InputBase } from '@mui/material';
import DynamicIcon from 'components/@2025/DynamicIcon';
import useHandleTrackEvent from '@/hooks/useHandleTrackEvent';
import { askAiLocation } from 'utils/askAiHandoff';

/**
 * The topbar's Ask AI field, Slack's search bar in position and weight.
 *
 * On a deal's page it starts with a chip naming that deal, and the question is
 * asked in that deal's context; removing the chip (its X, or Backspace in an
 * empty field) asks without one. Anywhere else -- All Deals / My Deals
 * included -- there is no deal to offer, so the question goes without deal
 * context. Enter opens the Ask AI page on a new chat carrying the question
 * (see `utils/askAiHandoff.js`), with a way back to where it was asked.
 */
const AskAiPrompt = () => {
  const theme = useTheme();
  const history = useHistory();
  const { pathname, search } = useLocation();
  const handleTrackEvent = useHandleTrackEvent();
  const inputRef = useRef(null);
  const iconsLibrary = useSelector((state) => state.uiStates.iconsLibrary);
  const selectedDealsLabel = useSelector(
    (state) => state.uiStates.selectedDealsLabel,
  );
  const loadedDeal = useSelector((state) => state.deals.dealById?.deal);
  const pinnedDeals = useSelector((state) => state.deals.pinnedDeals?.deals);

  const dealId = matchPath(pathname, { path: '/deals/:dealId' })?.params
    ?.dealId;
  // The deal page's own fetch, falling back to the pinned list so the chip
  // can show before that fetch lands for a pinned deal.
  const dealName =
    (dealId && loadedDeal?.id === dealId ? loadedDeal?.name : null) ||
    (dealId ? pinnedDeals?.find((deal) => deal?.id === dealId)?.name : null);

  const [text, setText] = useState('');
  const [chipRemoved, setChipRemoved] = useState(false);

  // Each deal page offers its own chip again, even after one was removed.
  useEffect(() => {
    setChipRemoved(false);
  }, [dealId]);

  const showDealChip = Boolean(dealId && dealName && !chipRemoved);

  const returnLabel = dealId
    ? dealName || 'deal'
    : /^\/deals\/?$/i.test(pathname)
    ? (selectedDealsLabel || 'All Deals').trim()
    : 'previous page';

  const submit = (e) => {
    const question = text.trim();
    if (!question) return;
    handleTrackEvent(e, 'topbar_ask_ai_submit');
    history.push(
      askAiLocation({
        text: question,
        dealId: showDealChip ? dealId : null,
        returnTo: { path: `${pathname}${search}`, label: returnLabel },
      }),
    );
    setText('');
    inputRef.current?.blur();
  };

  return (
    <Box
      data-testid="topbar-ask-ai"
      onClick={() => inputRef.current?.focus()}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: 1,
        maxWidth: 640,
        minWidth: 0,
        height: 28,
        paddingX: 1.25,
        borderRadius: 'var(--dc-radius-md)',
        backgroundColor: 'var(--dc-input-background)',
        border: '1px solid var(--dc-border)',
        cursor: 'text',
        transition: theme.transitions.create(['background-color', 'border'], {
          duration: theme.transitions.duration.shortest,
        }),
        '&:hover': { borderColor: 'var(--dc-border-strong)' },
        '&:focus-within': {
          backgroundColor: 'var(--dc-surface-raised)',
          borderColor: 'var(--dc-primary-text)',
        },
      }}
    >
      <DynamicIcon
        type={iconsLibrary || 'lucide'}
        name="askAI"
        color={theme.palette.text.secondary}
        width={18}
      />
      {showDealChip ? (
        <Chip
          data-testid="topbar-ask-ai-deal-chip"
          size="small"
          label={dealName}
          title={`Asking about ${dealName}`}
          onDelete={() => {
            setChipRemoved(true);
            inputRef.current?.focus();
          }}
          sx={{
            maxWidth: 220,
            flexShrink: 0,
            color: 'text.primary',
            backgroundColor: 'var(--dc-primary-soft)',
            '& .MuiChip-deleteIcon': {
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
            },
          }}
        />
      ) : null}
      <InputBase
        inputRef={inputRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit(e);
          } else if (e.key === 'Backspace' && !text && showDealChip) {
            setChipRemoved(true);
          } else if (e.key === 'Escape') {
            inputRef.current?.blur();
          }
        }}
        placeholder={
          showDealChip ? `Ask AI about ${dealName}` : 'Ask AI anything'
        }
        inputProps={{
          'aria-label': showDealChip
            ? `Ask AI about ${dealName}`
            : 'Ask AI anything',
          'data-testid': 'topbar-ask-ai-input',
          'data-trackid': 'topbar_ask_ai_submit',
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          typography: 'body2',
          color: 'text.primary',
          '& input::placeholder': {
            color: theme.palette.text.secondary,
            opacity: 1,
          },
        }}
      />
    </Box>
  );
};

export default AskAiPrompt;
